import { useState, useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useLocalization } from '@/hooks/useLocalization';
import { FooterButton } from '@/components/common/FooterButton';
import CashFlowTrendBarChart from '@/modules/cashFlow/cashFlowTrendBarChart';
import { CashFlowTabMode } from '@/modules/cashFlow/CashFlowDetailsBarChart';
import {
  TabItem,
  TabsToMobileSelect,
  TabsToMobileSelectPanel,
  useTabsToMobileSelectPanel,
} from '@/components/common/tabs-to-mobile-select/TabsToMobileSelect';
import {
  DataRangeFilterEnum,
  getDateRangeFromFilter,
  isAllTimeStartDate,
} from '@/constants/dates';
import {
  firstActiveMonthStart,
  trimEmptyTrendEdges,
} from '@/modules/cashFlow/trendRange';
import ChartCurrencyTitle from '@/components/charts/chartCurrencyTitle';
import {
  TransactionTrendsResponse,
  useGetTransactionTrendsQuery,
} from '@/store/transactionsApi';
import { WidgetTrendChartSkeleton } from '@/components/common/WidgetTrendChartSkeleton';
import { useWidgetDataUpdating } from '@/hooks/useWidgetDataUpdating';
import { useControlledDates } from '@/hooks/useControlledDates';
import DateRange from '@/components/date/DateRange';
import { StandardWidgetProps } from '@/models/WidgetModels';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useNavigate } from 'react-router';
import { useCanNavigateTo } from '@/hooks/useCanNavigateTo';
import { DASHBOARD_WIDGET_DATE_PICKER_SX } from '@/constants/dashboardWidgetDatePickerSx';
import { dashboardMobileWidthValue } from '@/constants';
import { useWidgetWidthIsBelow } from '@/hooks/useWidgetWidthIsBelow';
import { useEffectiveExclusions } from '@/hooks/useEffectiveExclusions';
import { ContentContainer, WIDGET_PADDING } from '../contentContainer';

interface CashFlowSummary {
  netValue: number;
  changeValue: number;
  percentageChange: number;
}

type DashboardCashFlowProps = StandardWidgetProps;

const isEmbeddableBundle = import.meta.env.VITE_WIDGET === 'external';

const VIEW_BY_MODES: CashFlowTabMode[] = ['both', 'income', 'spending'];

const VIEW_BY_TABS_MIN_WIDTH_PX = 400;

const DETAILS_TREND_TYPE: Record<Exclude<CashFlowTabMode, 'both'>, string> = {
  income: 'Income',
  spending: 'Spending',
};

const calculateCashFlowSummary = (
  data: TransactionTrendsResponse,
): CashFlowSummary => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const periodNets = (data.Records?.categories ?? []).map(
    (category) => category.value,
  );
  if (!periodNets.length) {
    return { netValue: 0, changeValue: 0, percentageChange: 0 };
  }

  const netValue = periodNets.reduce((sum, value) => sum + value, 0);
  const firstPeriodNet = periodNets[0];

  let lastNonZeroIndex = periodNets.length - 1;
  while (lastNonZeroIndex > 0 && periodNets[lastNonZeroIndex] === 0) {
    lastNonZeroIndex--;
  }

  const changeValue = periodNets[lastNonZeroIndex] - firstPeriodNet;
  const percentageChange =
    firstPeriodNet !== 0 ? changeValue / Math.abs(firstPeriodNet) : 0;

  return { netValue, changeValue, percentageChange };
};

const { startDate: initialStartDate, endDate: initialEndDate } =
  getDateRangeFromFilter(DataRangeFilterEnum.OpenYear);

export default function DashboardCashFlow({
  startDate: propStartDate,
  endDate: propEndDate,
  isWidget = false,
  onDataLoaded,
  title,
  container = 'widget',
}: DashboardCashFlowProps = {}) {
  const { t } = useLocalization();
  const [groupBy] = useState<DataRangeFilterEnum>(DataRangeFilterEnum.OpenYear);
  const authLoaded = useAuthIsLoaded();
  const navigate = useNavigate();
  const canViewCashFlow = useCanNavigateTo('/investor/CashFlow');
  const widgetTitle = isEmbeddableBundle
    ? t('widgetCashFlowTrend', 'Cash Flow Trend')
    : (title ?? t('Cash Flow'));
  const effectiveExclusions = useEffectiveExclusions();
  const { ref: widgetWidthRef, isBelow: isWidgetBelowNarrowDateField } =
    useWidgetWidthIsBelow(isWidget, dashboardMobileWidthValue);
  const widgetNarrowDateField = isWidget && isWidgetBelowNarrowDateField;
  const { ref: viewByWidthRef, isBelow: isViewByBelowTabsWidth } =
    useWidgetWidthIsBelow(isWidget, VIEW_BY_TABS_MIN_WIDTH_PX);
  const {
    instanceId: viewByInstanceId,
    selectedTabKey: viewByTabKey,
    handleTabChange: handleViewByChange,
  } = useTabsToMobileSelectPanel();
  const viewByMode: CashFlowTabMode =
    VIEW_BY_MODES.find((mode) => mode === viewByTabKey) ?? 'both';
  const viewByTabs: TabItem[] = useMemo(
    () => [
      { label: t('both'), key: 'both' },
      { label: t('incomeDetails'), key: 'income' },
      { label: t('spendingDetails'), key: 'spending' },
    ],
    [t],
  );
  // Use our new custom hook
  const { startDate, endDate, setStartDate, setEndDate } = useControlledDates({
    propStartDate: propStartDate,
    propEndDate: propEndDate,
    initialWidgetStartDate: initialStartDate,
    initialWidgetEndDate: initialEndDate,
    isWidget,
  });

  const trendsArgs = authLoaded
    ? {
        startDate: startDate as string,
        endDate: endDate as string,
        ...(effectiveExclusions.excludedAccounts.length > 0
          ? { ExcludedAccounts: effectiveExclusions.excludedAccounts }
          : {}),
        ...(effectiveExclusions.excludedAccountTypes.length > 0
          ? {
              ExcludedAccountTypes: effectiveExclusions.excludedAccountTypes,
            }
          : {}),
        ...(effectiveExclusions.excludedCategories.length > 0
          ? { ExcludedCategories: effectiveExclusions.excludedCategories }
          : {}),
      }
    : undefined;

  const {
    data: transactionTrendsData,
    // `data` holds the last successful response, which during a range change is
    // still the previous range's. Deciding anything about the selected range
    // from that settles it against the wrong months, so read `currentData`,
    // which is only ever the response for the range being asked about now.
    currentData: trendsForSelectedRange,
    isFetching,
    isLoading,
  } = useGetTransactionTrendsQuery(trendsArgs ?? skipToken);

  const {
    currentData: detailsForSelectedRange,
    isFetching: isFetchingDetails,
    isLoading: isLoadingDetails,
  } = useGetTransactionTrendsQuery(
    trendsArgs && viewByMode !== 'both'
      ? { ...trendsArgs, trendType: DETAILS_TREND_TYPE[viewByMode] }
      : skipToken,
  );

  useEffect(() => {
    if (transactionTrendsData) {
      onDataLoaded?.();
    }
  }, [transactionTrendsData, onDataLoaded]);

  const isChartUpdating = useWidgetDataUpdating({
    isFetching: isFetching || isFetchingDetails,
    isLoading: isLoading || isLoadingDetails,
    requireAuth: true,
  });

  const disableDatePicker = isEmbeddableBundle && isChartUpdating;

  /** The host's range, when it is the host driving the dates. */
  const hostStartDate = propStartDate?.trim();

  /** Whether the widget's own picker last chose All Time. */
  const [pickedAllTime, setPickedAllTime] = useState(false);

  /**
   * Whether the chart is showing All Time, read from the range that was asked
   * for rather than the one in effect. Settling rewrites the start date the
   * widget holds, so that date can no longer answer the question -- but the
   * prop, or the picker's own choice, still says what was requested.
   */
  const isAllTimeView = hostStartDate
    ? isAllTimeStartDate(hostStartDate)
    : pickedAllTime || isAllTimeStartDate(startDate);

  /**
   * All Time asks the API for everything since a floor far older than any real
   * transaction, so the response leads with decades of empty months. Charting
   * them pushed the account's actual history into the right-hand edge, dated
   * from January 1900 (EUD-901). Narrow it to the span that holds transactions.
   *
   * Only for All Time: any other range is the window the user asked for, and its
   * empty months are part of the answer.
   */
  const visibleTrendsData = useMemo(() => {
    if (!trendsForSelectedRange) return undefined;
    if (!isAllTimeView) return trendsForSelectedRange;
    return trimEmptyTrendEdges(trendsForSelectedRange);
  }, [trendsForSelectedRange, isAllTimeView]);

  const visibleDetailsData = useMemo(() => {
    if (!detailsForSelectedRange) return undefined;
    if (!isAllTimeView) return detailsForSelectedRange;
    return trimEmptyTrendEdges(detailsForSelectedRange);
  }, [detailsForSelectedRange, isAllTimeView]);

  /**
   * Settle the selected range on the account's own history once the data says
   * where it begins. Trimming fixes what the chart draws, but the range itself
   * still started at the All Time floor, so reopening the date picker dropped
   * the user in January 1900 (EUD-901).
   *
   * This runs at most once per All Time selection: the new start date is no
   * longer the floor, so the condition cannot hold again.
   */
  useEffect(() => {
    if (!trendsForSelectedRange) return;
    if (!isAllTimeStartDate(startDate)) return;

    const firstMonth = firstActiveMonthStart(trendsForSelectedRange);
    // No transactions at all: leave the range alone so the widget keeps showing
    // its own empty state rather than an invented one.
    if (!firstMonth) return;

    setStartDate(firstMonth);
  }, [trendsForSelectedRange, startDate, setStartDate]);

  const cashFlowSummary = visibleTrendsData
    ? calculateCashFlowSummary(visibleTrendsData)
    : undefined;

  const handleGroupByChange = (newStartDate: string, newEndDate: string) => {
    // const newGroupBy = event.target.value as DataRangeFilterEnum;
    // setGroupBy(newGroupBy);

    // The picked range is what the view is, whatever settling does to the start
    // date afterwards.
    setPickedAllTime(isAllTimeStartDate(newStartDate));
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  return (
    <ContentContainer
      ref={widgetWidthRef}
      sx={{ minWidth: { xs: '100%', sm: 350 }, textAlign: 'center' }}
      container={container}
      hasCustomPadding
    >
      <Box sx={{ p: WIDGET_PADDING }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2,
          }}
        >
          <Typography variant='h5'>{widgetTitle}</Typography>

          {isWidget && (
            <Box sx={DASHBOARD_WIDGET_DATE_PICKER_SX}>
              <DateRange
                startDate={startDate}
                endDate={endDate}
                onChange={handleGroupByChange}
                shortcutsFilter={(f) =>
                  f !== DataRangeFilterEnum.Trailing7Days &&
                  f !== DataRangeFilterEnum.Trailing30Days
                }
                useWidgetNarrowIconField={widgetNarrowDateField}
                disabled={disableDatePicker}
              />
            </Box>
          )}
        </Box>
        <Box
          ref={viewByWidthRef}
          sx={{ mb: 2 }}
        >
          <TabsToMobileSelect
            selectedTabKey={viewByTabKey}
            tabs={viewByTabs}
            handleTabChange={handleViewByChange}
            instanceId={viewByInstanceId}
            ariaLabel={t('cashFlowDetails.tabsAria')}
            forceSelect={isViewByBelowTabsWidth}
          />
        </Box>
        {VIEW_BY_MODES.map((mode) => (
          <TabsToMobileSelectPanel
            key={mode}
            tabKey={mode}
            selectedTabKey={viewByTabKey}
            instanceId={viewByInstanceId}
          >
            {isChartUpdating ? (
              <WidgetTrendChartSkeleton />
            ) : (
              <>
                <ChartCurrencyTitle
                  totalValue={cashFlowSummary?.netValue || 0}
                  otherValue={cashFlowSummary?.changeValue || 0}
                  percentage={cashFlowSummary?.percentageChange || 0}
                  compareText={groupBy}
                />

                <CashFlowTrendBarChart
                  transactionTrendsData={
                    viewByMode === 'both'
                      ? visibleTrendsData
                      : visibleDetailsData
                  }
                  mode={viewByMode}
                />
              </>
            )}
          </TabsToMobileSelectPanel>
        ))}
      </Box>
      {isWidget && canViewCashFlow && !isEmbeddableBundle && (
        <FooterButton
          buttonText={t('viewCashFlow')}
          onClick={() => {
            void navigate('/investor/CashFlow');
          }}
        />
      )}
    </ContentContainer>
  );
}
